import type {
  FPlusBranchTypeCheckingContext,
  FPlusEmptyTypeCheckingContext,
  FPlusImportedConfigurationTypeCheckingContext,
  FPlusWorkspaceTypeCheckingContext,
} from '@festina/configuration-general-types/configuration'
import { STANDARD_BRANCH } from '@festina-life/api'
import { type ReactNode, useMemo } from 'react'
import { useParams } from 'react-router'

import type { AnyConfigurationRouteParams } from '../system/types'
import { useNavigationParams } from '../system/useNavigationParams'
import { getParameterDefinitionAtDate } from '../uiModels/parameterOperations/helpers'
import { getTemplateOperationAtDate } from '../uiModels/templateOperations/helpers'
import { useApplicationViewOperationsContext } from '../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../workspace/operationsContext'
import { useConfigurationContext } from './configurationContext'
import { Provider } from './fPlusTypeCheckingContext'

type Props = {
  children: ReactNode
}

const FPlusTypeCheckingContextProvider = ({ children }: Props) => {
  const { branchKey, workspaceKey } = useOperationsContext()
  const { applicationTemplates } = useApplicationViewOperationsContext()

  const { viewAt: at, viewAtString: atString } = useConfigurationContext()
  const { server, layer, block } = useNavigationParams()
  const { templateId, parameterDefinitionId } =
    useParams<AnyConfigurationRouteParams>()
  const { applicationParameterDefinitions } =
    useApplicationViewOperationsContext()

  const currentOperation = useMemo(() => {
    if (templateId) {
      return (
        getTemplateOperationAtDate(applicationTemplates.get(templateId), at)
          ?.internalOperation ?? undefined
      )
    } else if (parameterDefinitionId) {
      return (
        getParameterDefinitionAtDate(
          applicationParameterDefinitions.get(parameterDefinitionId),
          at,
        )?.internalOperation ?? undefined
      )
    }

    return undefined
  }, [
    applicationParameterDefinitions,
    applicationTemplates,
    templateId,
    parameterDefinitionId,
    at,
  ])

  const context = useMemo(() => {
    if (workspaceKey) {
      return {
        at: atString,
        _type: 'FPlusWorkspaceTypeCheckingContext',
        workspaceName: workspaceKey.name,
        componentName: workspaceKey.branchKey.server,
        layer: layer.layerId,
        block,
        tenantId: layer.tenantId,
      } as FPlusWorkspaceTypeCheckingContext
    }

    if (branchKey && branchKey.name !== STANDARD_BRANCH) {
      return {
        at: atString,
        _type: 'FPlusBranchTypeCheckingContext',
        branchName: branchKey.name,
        block,
        layer: layer.layerId,
        tenantId: layer.tenantId,
        componentName: server.toLocaleLowerCase(),
      } as FPlusBranchTypeCheckingContext
    }

    if (branchKey && branchKey.name === STANDARD_BRANCH) {
      return {
        at: atString,
        _type: 'FPlusImportedConfigurationTypeCheckingContext',
      } as FPlusImportedConfigurationTypeCheckingContext
    }

    return {
      _type: 'FPlusEmptyTypeCheckingContext',
    } as FPlusEmptyTypeCheckingContext
  }, [
    workspaceKey,
    branchKey,
    server,
    atString,
    layer.layerId,
    layer.tenantId,
    block,
  ])

  return (
    <Provider
      value={{
        context,
        currentOperation,
      }}
    >
      {children}
    </Provider>
  )
}

export { FPlusTypeCheckingContextProvider }
